import i18next from 'i18next'

class I18n {
  /**
   * Get active language
   * @returns {String} language
   */
  get language() {
    return i18next.language
  }

  /**
   * Get registered languages
   * @returns {Array} languages
   */
  get languages() {
    return i18next.languages
  }
  /**
   * Get current options
   * @returns {Object} options
   */
  get options() {
    return i18next.options
  }

  /**
   * Is initialized getter
   * @returns {Boolean} initialize
   */
  get isInitialized() {
    return i18next.isInitialized
  }

  /**
   * Events store getter
   * @returns {Object}
   */
  get store() {
    return i18next.store
  }

  /**
   * Initialize translator
   * @param {Object} options options
   * @param {Function} callback callback
   */
  init({ onLanguageChanged, ...options }, callback = null) {
    i18next.init(options, callback)

    this._onLanguageChanged = onLanguageChanged
  }

  /**
   * Create new instance
   * @param {*} options options
   * @param {*} callback callback
   */
  newInstance(options = {}, callback) {
    return new I18n(options, callback)
  }

  /**
   * Extend translator functionalities with modules
   * @param {any} module module
   */
  use(module) {
    i18next.use(module)

    return this
  }

  /**
   * Get translation fo given key
   * @param {String | Array} key key
   * @param {Options} options options
   * @returns {Any}
   */
  t(key, options = {}) {
    return i18next.t(key, options)
  }

  /**
   * Change active language
   * @param {*} lng language
   * @param {*} callback callback
   */
  changeLanguage(lng, callback = null) {
    i18next.changeLanguage(lng, callback)

    if (this._onLanguageChanged) this._onLanguageChanged(lng)
  }

  /**
   * Adds one key/value.
   * @param {String} lng language
   * @param {String} ns namespace
   * @param {String} key key
   * @param {Any} value value
   * @param {Object} options options
   */
  addResource(lng, ns, key, value, options) {
    i18next.addResource(lng, ns, key, value, options)
  }

  /**
   * Adds multiple key/values.
   * @param {String} lng language
   * @param {String} ns namespace
   * @param {Any} resources resources
   */
  addResources(lng, ns, resources) {
    i18next.addResources(lng, ns, resources)
  }

  /**
   * Adds module resources
   * @param {String} ns namespace
   * @param {Object} resources resources
   * @param {Object} options options
   */
  addModuleResource(ns, resources, options) {
    for (let lng in resources) {
      if (resources.hasOwnProperty(lng)) {
        this.addResources(lng, ns, resources[lng], options)
      }
    }
  }

  /**
   * Add resources after translator initialization
   * @param {String} lng language
   * @param {String} ns namespace
   * @param {Any} resources resources
   * @param {Boolean} deep deep
   * @param {Boolean} overwrite overwrite
   * @returns {i18n}
   */
  addResourceBundle(lng, ns, resources, deep = false, overwrite = false) {
    i18next.addResourceBundle(lng, ns, resources, deep, overwrite)

    return this
  }

  /**
   * Get resource
   * @param {String} lng language
   * @param {String} ns namespace
   * @param {String} key key
   * @param {Object} options options
   */
  getResource(lng, ns, key, options) {
    return i18next.getResources(lng, ns, key, options)
  }

  /**
   * Get a translator instance with fixed language and namespace
   * @param {String} lng language
   * @param {String} ns namespace
   */
  getFixedT(lng, ns = null) {
    const that = this

    const fixedT = (key, opts, ...rest) => {
      let options = { ...opts }
      if (typeof opts !== 'object') {
        options = that.options.overloadTranslationOptionHandler(
          [key, opts].concat(rest)
        )
      }

      options.lng = options.lng || fixedT.lng
      options.lngs = options.lngs || fixedT.lngs
      options.ns = options.ns || fixedT.ns

      return that.t(key, options)
    }

    if (typeof lng === 'string') {
      fixedT.lng = lng
    } else {
      fixedT.lngs = lng
    }
    fixedT.ns = ns

    return fixedT
  }

  /**
   * Load namespaces
   * @param {String} ns namespace
   * @param {Function} callback callback
   */
  loadNamespaces(ns, callback = null) {
    i18next.loadNamespaces(ns, callback)
  }

  /**
   * Load languages after first initialize
   * @param {Array|String} lngs language 's
   * @param {Function} callback callback
   */
  loadLanguages(lngs, callback = null) {
    i18next.loadLanguages(lngs, callback)
  }

  /**
   * Reload languages and namespaces
   * @param {Array|String} lngs language 's
   * @param {Array|String} ns namespace 's
   */
  reloadResources(lngs = null, ns = null) {
    i18next.reloadResources(lngs, ns)
  }

  /**
   * Set default namespace
   * @param {String} ns namespace
   */
  setDefaultNamespace(ns) {
    i18next.setDefaultNamespace(ns)
  }

  /**
   * Returns rtl or ltr depending on languages read direction.
   * @param {String} lng language
   * @return string
   */
  dir(lng) {
    return i18next.dir(lng)
  }
}

export default new I18n()
